import { Task } from '@/types/tasks';
import {
	BaseTransaction,
	type Vendor,
} from '@/types/transactions';
import { getIsoDateString } from '@/utils/dates';
import {
	type AgreementStatus,
	type ApiV1,
	type OnboardingStatus,
	type TransactionPaymentType,
	type TransactionType,
	type TransactionUnitType,
} from '@teamworksdev/influencer/core';
import { type MediaAttachment } from '@teamworksdev/vuetify';
import { marked } from 'marked';
import { isNil } from 'lodash-es';
import type { Uuid } from '@/types/utilities';

class BaseAgreement {
	amount: number;
	name: string;
	status: AgreementStatus;
	transactionsCount: number;
	uuid: Uuid;
	acceptedBy: string | null = null;
	acceptedAt: string | null = null;
	contractId: string | null;

	constructor(data:
		| ApiV1.Agreement
		| ApiV1.ListAgreement
		| ApiV1.MessageModelAgreement
		| ApiV1.RecentAgreement
		| NonNullable<ApiV1.BaseTransaction['agreement']>,
	) {
		this.amount = data.amount;
		this.name = data.name;
		this.status = data.status;
		this.transactionsCount = data.transactions_count;
		this.uuid = data.uuid;
		this.contractId = 'contract_id' in data ? data.contract_id : null;

		if ('accepted_at' in data) {
			this.acceptedAt = data.accepted_at;
		}

		if ('accepted_by' in data) {
			this.acceptedBy = data.accepted_by;
		}
	}
}


class ContractParticipant {
	name: string;
	email: string | null;
	isPayee: boolean;
	signingOrder: number;
	signingGroupTwrn: string | null;
	contractRecipientTwrn: string | null;

	latestStatus: ApiV1.SignatureStatus | null;
	latestUpdatedAt: string | null;

	constructor(data: ApiV1.AgreementParticipantResponse) {
		this.name = data.name;
		this.email = data.email;
		this.isPayee = data.is_payee;
		this.signingOrder = data.signing_order;
		this.signingGroupTwrn = data.signing_group_twrn;
		this.contractRecipientTwrn = data.contract_recipient_twrn;
		this.latestStatus = isNil(data.latest_status) ? null : data.latest_status;
		this.latestUpdatedAt = data.latest_updated_at ?? null;
	}
}
class ListAgreement extends BaseAgreement {
	payeeName: string;
	payeeUuid: Uuid;
	updatedAt: string;
	tasksOverview: ApiV1.TasksOverview | undefined;
	category: TransactionType;

	constructor(data: ApiV1.ListAgreement) {
		super(data);
		this.payeeName = data.payee.name;
		this.payeeUuid = data.payee.uuid;
		this.updatedAt = data.updated_at;
		if (data.tasks) {
			this.tasksOverview = {
				completed: data.tasks.completed,
				total: data.tasks.total,
				percentage: (data.tasks.percentage ?? 0) / 100,
			};
		}
		this.category = data.category;
	}
}

class Agreement extends BaseAgreement {
	attachments: ApiV1.Media[];
	category: TransactionType;
	createdAt: string;
	description: string;
	expiresAt: string | null;
	approvalRequired: boolean;
	payeeName: string;
	payeeUuid: Uuid;
	onboardingStatus?: OnboardingStatus;
	tasks: Task[];
	tasksOverview: ApiV1.TasksOverview;
	transactions: BaseTransaction[];
	updatedAt: string;
	vendor: Vendor;
	unitType: TransactionUnitType;
	paymentType: TransactionPaymentType;
	contractId: string | null;
	participants: ContractParticipant[];

	constructor(data: ApiV1.Agreement) {
		super(data);
		this.attachments = data.attachments;
		this.category = data.category;
		this.createdAt = data.created_at;
		this.description = data.description;
		this.expiresAt = getIsoDateString(data.expires_at) ?? null;
		this.approvalRequired = data.approval_required;
		this.payeeName = data.payee.name;
		this.payeeUuid = data.payee.uuid;
		this.onboardingStatus = data.payee.onboarding_status;
		this.paymentType = data.payment_type;
		this.tasks = data.tasks.map((task) => new Task(task));
		this.contractId = data.contract_id;
		this.participants = data.participants?.map((p) => new ContractParticipant(p)) ?? [];

		this.tasksOverview = {
			completed: 0,
			total: 0,
			percentage: null,
		};

		if (data.tasks.length) {
			const completedTasks = data.tasks.filter((task) => task.completed_at !== null).length;
			const totalTasks = data.tasks.length;
			this.tasksOverview = {
				completed: completedTasks,
				total: totalTasks,
				percentage: completedTasks / totalTasks,
			};
		}

		this.transactions = data.transactions.map((transaction) => new BaseTransaction(transaction));
		this.updatedAt = data.updated_at;
		this.vendor = {
			email: data.vendor.email,
			name: data.vendor.name,
			phone: data.vendor.phone,
			pointOfContact: data.vendor.point_of_contact,
		};
		this.unitType = data.unit_type;
	}

	get descriptionHtml() {
		return marked(this.description, { async: false });
	}
}

class RecentAgreement extends BaseAgreement {
	category: TransactionType;

	constructor(data: ApiV1.RecentAgreement | ApiV1.MessageModelAgreement) {
		super(data);
		this.category = data.category;
	}
}

type AgreementFormTaskModel = ApiV1.CreateAgreementRequestModelTask & {
	taskUuid?: string;
	existingAttachments?: MediaAttachment[];
};

interface AgreementFormTaskUpdate {
	taskUuid: string;
	fields?: Pick<ApiV1.UpdateTaskModel, 'name' | 'description' | 'due_at'>;
	newAttachments?: ApiV1.MediaAttachment[];
	removedAttachmentIds?: string[];
}

export { Agreement, BaseAgreement, ContractParticipant, ListAgreement, RecentAgreement };
export type { AgreementFormTaskModel, AgreementFormTaskUpdate };
